import { useState, useEffect } from 'react';
import {
  useFloor,
  useCreateFloor,
  useUpdateFloor,
} from '../../services/httpServices/queries';
import {
  Loader2,
  AlertCircle,
  Plus,
  Save,
  X,
  Building,
} from 'lucide-react';

interface FloorFormProps {
  isOpen: boolean;
  floorId?: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function FloorForm({ isOpen, floorId, onClose, onSuccess }: FloorFormProps) {
  const isEdit = !!floorId;

  const { data: floorData, isLoading: floorLoading, error: floorError } = useFloor(floorId);
  const createMutation = useCreateFloor();
  const updateMutation = useUpdateFloor();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    floorNumber: '',
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const floor = floorData?.data;

  useEffect(() => {
    if (isEdit && floor) {
      setFormData({
        name: floor.name,
        description: floor.description || '',
        floorNumber: String(floor.floorNumber ?? ''),
      });
    } else if (!isEdit) {
      setFormData({ name: '', description: '', floorNumber: '' });
    }
  }, [isEdit, floor]);

  useEffect(() => {
    if (isOpen) {
      setFormErrors({});
      if (!isEdit) {
        setFormData({ name: '', description: '', floorNumber: '' });
      }
    }
  }, [isOpen, isEdit]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (formData.floorNumber === '' || isNaN(Number(formData.floorNumber))) {
      errors.floorNumber = 'Valid floor number is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const data = {
      name: formData.name,
      description: formData.description || undefined,
      floorNumber: Number(formData.floorNumber),
    };

    try {
      if (isEdit) {
        await updateMutation.mutateAsync({ id: floorId!, data });
      } else {
        await createMutation.mutateAsync(data);
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Something went wrong';
      setFormErrors({ general: message });
    }
  };

  const updateField = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const inputBase = 'w-full px-3 py-2 border rounded-lg bg-background text-text focus:outline-none focus:ring-2 text-sm transition-all';
  const inputValid = 'border-border focus:ring-primary';
  const inputInvalid = 'border-danger focus:ring-danger';
  const labelClass = 'block text-sm font-medium text-text mb-1';

  const ic = (field: string) => `${inputBase} ${formErrors[field] ? inputInvalid : inputValid}`;
  const err = (field: string) => formErrors[field] ? <p className="text-xs text-danger mt-1">{formErrors[field]}</p> : null;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-surface rounded-xl border border-border w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="sticky top-0 bg-surface z-10 flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Building size={18} className="text-primary" />
            <h2 className="text-lg font-semibold text-text">
              {isEdit ? 'Edit Floor' : 'Add Floor'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-background text-text-muted transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4">
          {isEdit && floorLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          )}

          {isEdit && (floorError || !floor) && !floorLoading && (
            <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
              <AlertCircle size={18} />
              <span>Failed to load floor details. Please try again.</span>
            </div>
          )}

          {(!isEdit || (floor && !floorLoading)) && (
            <form id="floor-form" onSubmit={handleSubmit} className="space-y-4">
              {formErrors.general && (
                <div className="bg-danger/10 text-danger rounded-lg p-4 flex items-center gap-2">
                  <AlertCircle size={18} />
                  <span className="text-sm font-medium">{formErrors.general}</span>
                </div>
              )}

              <div>
                <label className={labelClass}>Name *</label>
                <input
                  type="text"
                  className={ic('name')}
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="1st Floor"
                />
                {err('name')}
              </div>

              <div>
                <label className={labelClass}>Floor Number *</label>
                <input
                  type="number"
                  className={ic('floorNumber')}
                  value={formData.floorNumber}
                  onChange={(e) => updateField('floorNumber', e.target.value)}
                  placeholder="1"
                />
                {err('floorNumber')}
              </div>

              <div>
                <label className={labelClass}>Description</label>
                <textarea
                  className={`${inputBase} ${inputValid}`}
                  value={formData.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  rows={3}
                  placeholder="Optional description of the floor..."
                />
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-surface z-10 flex items-center justify-end gap-2 p-4 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-border rounded-lg hover:bg-background transition-colors text-sm font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="floor-form"
            disabled={createMutation.isPending || updateMutation.isPending || (isEdit && floorLoading)}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2 shadow-sm"
          >
            {(createMutation.isPending || updateMutation.isPending) && <Loader2 size={16} className="animate-spin" />}
            {isEdit ? <Save size={16} /> : <Plus size={16} />}
            {isEdit ? 'Save Changes' : 'Add Floor'}
          </button>
        </div>
      </div>
    </div>
  );
}
